import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useRouteMatch } from "react-router-dom";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Button, Col, Row, Container } from "reactstrap";
import "./index.css";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import style from "react-syntax-highlighter/dist/esm/styles/hljs/agate";
import { UserInfo } from "model/user";
import api from "api";
import { ApiResponse } from "model/response";
import {
  ScheduleQueryDetailResponse,
  ScheduleQuery,
} from "model/schedule_query";
import { Popup } from "components/Popup";

interface DetailData {
  scheduleQuery: ScheduleQuery | null;
  loading: boolean;
}

interface ModalData {
  isOpen: boolean;
  title: string;
  type: string;
  description: string;
  onPressSubmit: Function;
  onPressCancel: Function;
}

export default function ScheduleQueryDetail(props: any) {
  const match = useRouteMatch("/schedule_query/:schedule_query_id");
  const params: any = match?.params;
  const scheduleQueryId = params?.schedule_query_id;

  const companyId: any = api.getCurrentCompanyId();
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [detailData, setDetailData] = useState<DetailData>({
    scheduleQuery: null,
    loading: true,
  });
  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    title: "",
    type: "confirm",
    description: "",
    onPressCancel: () => {},
    onPressSubmit: () => {},
  });

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
    const res: ApiResponse = await ApiCaller.get(
      `schedule_query/${scheduleQueryId}`
    );
    if (res?.status === "ok") {
      const data: ScheduleQueryDetailResponse = res?.data;
      setDetailData({
        ...detailData,
        ...{
          scheduleQuery: data?.schedule_query,
          loading: false,
        },
      });
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
  };

  const changeStateScheduleQuery = async () => {
    setDetailData({
      ...detailData,
      ...{
        loading: true,
      },
    });
    let res: ApiResponse;
    const currentState = detailData?.scheduleQuery?.is_active;
    if (currentState) {
      res = await ApiCaller.delete(`schedule_query/${scheduleQueryId}/disable`);
    } else {
      res = await ApiCaller.post(`schedule_query/${scheduleQueryId}/enable`);
    }
    if (res?.status === "ok") {
      const newScheduleQuery: any = {
        ...detailData.scheduleQuery,
        ...{ is_active: !currentState },
      };
      setDetailData({
        ...detailData,
        ...{
          loading: false,
          scheduleQuery: newScheduleQuery,
        },
      });
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
    setModalData({ ...modalData, isOpen: false });
  };
  
  const runDataPipeline = async () => {
    const res:any = await ApiCaller.post(
      `schedule_query/${scheduleQueryId}/execute?company_id=${companyId}`
    );
    alert(res.message);
  };

  const checkDataPipelineStatus = async () => {
    const res:any = await ApiCaller.get(
      `schedule_query/${scheduleQueryId}/status?company_id=${companyId}`
    );
    alert(res.message);
  };

  const deleteScheduleQuery = async () => {
    setDetailData({
      ...detailData,
      ...{
        loading: true,
      },
    });
    const res: ApiResponse = await ApiCaller.delete(
      `schedule_query/${scheduleQueryId}`
    );
    if (res?.status === "ok") {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
      props.history.goBack();
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
    setModalData({ ...modalData, isOpen: false });
  };

  if (detailData?.loading) return <Loading />;

  return (
    <Container>
      <Popup
        type={modalData?.type}
        isOpen={modalData?.isOpen}
        title={modalData?.title}
        description={modalData?.description}
        onPressSubmit={modalData?.onPressSubmit}
        onPressCancel={modalData?.onPressCancel}
      />
      <Row>
        <Col xs={12}>
          <h3>
            {detailData?.scheduleQuery?.name}
          </h3>
        </Col>
        <Col xs={12}>
          <div style={styles.controls}>
            <Button
              className="schedule-query__button"
              size="sm"
              color="primary"
              tag={Link}
              to={{ pathname: `/schedule_query/${scheduleQueryId}/edit` }}
            >
              Edit
            </Button>

            <Button
              className="schedule-query__button"
              size="sm"
              color="primary"
              onClick={runDataPipeline}
              disabled={detailData?.scheduleQuery?.is_active ? false : true}
            >
              Trigger Run
            </Button>

            <Button
              className="schedule-query__button"
              size="sm"
              color="info"
              onClick={checkDataPipelineStatus}
            >
              Check Status
            </Button>

            <Button
              className="schedule-query__button"
              onClick={() => {
                setModalData({
                  isOpen: true,
                  title: "Warning",
                  type: "confirm",
                  description: `Are you sure want to ${
                    detailData?.scheduleQuery?.is_active ? "disable" : "enable"
                  } this item ?`,
                  onPressSubmit: changeStateScheduleQuery,
                  onPressCancel: () => {
                    setModalData({ ...modalData, ...{ isOpen: false } });
                  },
                });
              }}
              size="sm"
              color={
                detailData?.scheduleQuery?.is_active ? "warning" : "secondary"
              }
            >
              {detailData?.scheduleQuery?.is_active ? "Disable" : "Enable"}
            </Button>

            <Button
              className="schedule-query__button"
              size="sm"
              color="danger"
              onClick={() => {
                setModalData({
                  isOpen: true,
                  title: "Warning",
                  type: "confirm",
                  description: `Are you sure want to delete this item ?`,
                  onPressSubmit: deleteScheduleQuery,
                  onPressCancel: () => {
                    setModalData({ ...modalData, ...{ isOpen: false } });
                  },
                });
              }}
            >
              Delete
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div style={styles.info}>
            <span>Query info</span>
            <ul>
              <li>
                <span style={styles.infoHeading}>Project name</span>:{" "}
                {
                  currentUser?.companies.find(
                    (comapny) => comapny.id == companyId
                  )?.name
                }
              </li>
              <li>
                <span style={styles.infoHeading}>Destination dataset</span>:{" "}
                {detailData?.scheduleQuery?.dataset_name}
              </li>
              <li>
                <span style={styles.infoHeading}>Destination table</span>:{" "}
                {detailData?.scheduleQuery?.table_name}
              </li>
              <li>
                <span style={styles.infoHeading}>User</span>:{" "}
                {detailData?.scheduleQuery?.full_name}
              </li>
              <li>
                <span style={styles.infoHeading}>Schedule time</span>:{" "}
                {detailData?.scheduleQuery?.schedule_time}
              </li>
              <li>
                <span style={styles.infoHeading}>Created at (UTC)</span>:{" "}
                {detailData?.scheduleQuery?.created_on}
              </li>
              <li>
                <span style={styles.infoHeading}>Updated at (UTC)</span>:{" "}
                {detailData?.scheduleQuery?.changed_on}
              </li>
            </ul>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <span>Query SQL</span>
          <div style={styles.queryContainer}>
            <SyntaxHighlighter
              language="sql"
              style={docco}
            >
              {detailData?.scheduleQuery?.query || ""}
            </SyntaxHighlighter>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

const styles: { [name: string]: React.CSSProperties } = {
  controls: {
    marginTop: 12,
    marginBottom: 12,
  },
  info: {
    marginTop: 12,
    marginBottom: 12,
  },
  infoHeading: {
    fontWeight: 700,
  },
  queryContainer: {
    marginTop: 12,
  },
};
